/**
 * React Query hooks for Pool operations
 */

import { useQuery, UseQueryResult, keepPreviousData } from '@tanstack/react-query';
import poolService from '../services/pool.service';
import type { Pool, PoolListResponse } from '../types';

interface UsePoolListQueryOptions {
  workspaceId?: string;
  idToken?: string;
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}

/**
 * Hook to query list of pools with pagination
 */
export function usePoolListQuery({
  workspaceId,
  idToken,
  page = 1,
  size = 100,
  sortBy = 'created_at',
  order = 'desc',
  search,
  enabled = true,
  refetchInterval = 5000,
}: UsePoolListQueryOptions): UseQueryResult<PoolListResponse, Error> {
  return useQuery<PoolListResponse, Error>({
    queryKey: ['pools', workspaceId, page, size, sortBy, order, search],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and ID token are required');
      }
      return poolService.listPools(workspaceId, idToken, {
        page,
        size,
        sortBy,
        order,
        search,
      });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    placeholderData: keepPreviousData,
    refetchInterval,
  });
}

interface UsePoolDetailQueryOptions {
  poolId?: string;
  workspaceId?: string;
  idToken?: string;
  enabled?: boolean;
}

/**
 * Hook to query a single pool by ID
 */
export function usePoolDetailQuery({
  poolId,
  workspaceId,
  idToken,
  enabled = true,
}: UsePoolDetailQueryOptions): UseQueryResult<Pool, Error> {
  return useQuery<Pool, Error>({
    queryKey: ['pool', poolId, workspaceId],
    queryFn: async () => {
      if (!poolId || !workspaceId || !idToken) {
        throw new Error('Pool ID, Workspace ID, and ID token are required');
      }
      return poolService.getPool(poolId, workspaceId, idToken);
    },
    enabled: enabled && !!poolId && !!workspaceId && !!idToken,
    staleTime: 30000,
  });
}
